<template>
  <div
    ref="pullContainer"
    class="relative  w-full touch-pan-y"
  >
    <!-- Custom Spinner (Twitter style: simple, centered at the top) -->
    <div
      class="absolute top-0 left-0 right-0 flex justify-center items-center z-50 transition-transform duration-200 ease-out"
      :style="{
        transform: `translateY(${Math.min(pullDistance - 40, 20)}px)`,
        opacity: pullDistance > 10 || isRefreshing ? 1 : 0
      }"
    >
      <div
        class="bg-surface rounded-full shadow-lg p-2.5 flex items-center justify-center border border-border transition-all duration-300"
        :class="{ 'animate-spin': isRefreshing, 'scale-100': pullDistance > 10, 'scale-50': pullDistance <= 10 && !isRefreshing }"
      >
        <i
          class="pi pi-spinner text-indigo-400 text-xl"
          :class="{ 'pi-spin': isRefreshing }"
          :style="{ transform: !isRefreshing ? `rotate(${pullDistance * 4}deg)` : 'none' }"
        ></i>
      </div>
    </div>

    <!-- Wrapped Content -->
    <!--
      El transform se aplica SOLO mientras se está tirando/refrescando (y durante
      el snap-back animado de vuelta). En reposo NO debe haber transform: un
      `transform` distinto de `none` crea un containing block y hace que los
      `position: fixed` internos (modales) se posicionen respecto a este div
      (alto = toda la página) en vez del viewport, apareciendo fuera de pantalla
      en mobile. `hasTransform` se apaga recién al terminar la transición para no
      perder la animación de retorno.
    -->
    <div
      class=" w-full transition-transform duration-200 ease-out"
      :style="hasTransform ? { transform: `translateY(${pullDistance}px)` } : null"
      @transitionend="onSnapBackEnd"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const pullContainer = ref(null);
const startY = ref(0);
const pullDistance = ref(0);
const isPulling = ref(false);
const isRefreshing = ref(false);
// Mantiene el transform aplicado mientras hay gesto o animación de retorno en
// curso; se apaga en transitionend para dejar el contenedor sin transform en
// reposo (ver comentario en el template).
const hasTransform = ref(false);

// Al terminar el snap-back (volvimos a 0 sin estar refrescando), quitamos el
// transform para que los modales `fixed` vuelvan a referenciar el viewport.
const onSnapBackEnd = (e) => {
  // Ignorar transitionend que burbujean desde hijos del slot: solo nos importa
  // la transición de `transform` de este mismo contenedor.
  if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
  if (pullDistance.value === 0 && !isRefreshing.value) {
    hasTransform.value = false;
  }
};

const THRESHOLD = 70; // Pixel distance needed to trigger refresh
const MAX_PULL = 100; // Max visual distance the pull can stretch
const RESISTANCE = 0.4; // Multiplier to make it feel like there's tension

const handleTouchStart = (e) => {
  // Only allow pull-to-refresh if we are at the very top of the page
  if (window.scrollY <= 0 && !isRefreshing.value) {
    startY.value = e.touches[0].clientY;
    isPulling.value = true;
  }
};

const handleTouchMove = (e) => {
  if (!isPulling.value) return;

  const currentY = e.touches[0].clientY;
  const distance = currentY - startY.value;

  // Only apply pull distance if we are pulling downwards from the top
  if (distance > 0 && window.scrollY <= 0) {
    // Prevent default scrolling to stop native bounce/refresh
    if (e.cancelable) {
      e.preventDefault();
    }

    // Dampen the visual pull to simulate tension
    hasTransform.value = true;
    pullDistance.value = Math.min(distance * RESISTANCE, MAX_PULL);
  } else {
    // If scrolled up or not at top, cancel the pull
    pullDistance.value = 0;
  }
};

const handleTouchEnd = () => {
  if (!isPulling.value) return;
  isPulling.value = false;

  if (pullDistance.value >= THRESHOLD) {
    // Trigger refresh
    isRefreshing.value = true;
    pullDistance.value = 50; // Keep spinner visible below header

    // Reload after short delay to show the spinner spinning
    setTimeout(() => {
      window.location.reload(true);
    }, 600);
  } else if (pullDistance.value > 0) {
    // Snap back animado; transitionend limpiará el transform al llegar a 0.
    pullDistance.value = 0;
  } else {
    // Se soltó ya en 0 (sin movimiento neto): no hay transición que dispare
    // transitionend, así que quitamos el transform directamente.
    hasTransform.value = false;
  }
};

onMounted(() => {
  if (pullContainer.value) {
    // Adding non-passive touchmove is required for e.preventDefault() to work reliably
    pullContainer.value.addEventListener('touchstart', handleTouchStart, { passive: true });
    pullContainer.value.addEventListener('touchmove', handleTouchMove, { passive: false });
    pullContainer.value.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
});

onUnmounted(() => {
  if (pullContainer.value) {
    pullContainer.value.removeEventListener('touchstart', handleTouchStart);
    pullContainer.value.removeEventListener('touchmove', handleTouchMove);
    pullContainer.value.removeEventListener('touchend', handleTouchEnd);
  }
});
</script>
